import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { LogoMark } from "../components/Logo";
import { ArrowRight, Lock, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError(t.auth.reset_invalid_link);
      return;
    }
    if (password.length < 8) {
      setError(t.auth.reset_password_too_short);
      return;
    }
    if (password !== confirm) {
      setError(t.auth.reset_password_mismatch);
      return;
    }

    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 bg-[#F8F9FA]">
      <div className="w-full max-w-md bg-white border border-neutral-200 p-8 sm:p-10" data-testid="reset-password-card">
        <div className="w-fit mb-6"><LogoMark size={48} /></div>

        {!token ? (
          <>
            <div className="w-12 h-12 bg-red-100 text-red-600 flex items-center justify-center rounded-sm mb-5">
              <AlertCircle size={24} />
            </div>
            <h1 className="text-3xl font-black tracking-tighter mb-3">{t.auth.reset_invalid_title}</h1>
            <p className="text-sm text-neutral-600 leading-relaxed mb-6">{t.auth.reset_invalid_body}</p>
            <Link to="/forgot-password" className="btn-primary w-full justify-center">
              {t.auth.forgot_title} <ArrowRight size={16} />
            </Link>
          </>
        ) : done ? (
          <>
            <div className="w-12 h-12 bg-[#10B981]/10 text-[#10B981] flex items-center justify-center rounded-sm mb-5">
              <CheckCircle2 size={24} />
            </div>
            <h1 className="text-3xl font-black tracking-tighter mb-3" data-testid="reset-success-title">{t.auth.reset_success_title}</h1>
            <p className="text-sm text-neutral-600 leading-relaxed mb-6">{t.auth.reset_success_body}</p>
            <Link to="/login" className="btn-primary w-full justify-center" data-testid="reset-go-login">
              {t.auth.sign_in_btn} <ArrowRight size={16} />
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tighter mb-2" data-testid="reset-title">{t.auth.reset_title}</h1>
            <p className="text-sm text-neutral-600 mb-7">{t.auth.reset_subtitle}</p>

            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="password"
                  required
                  minLength={8}
                  placeholder={t.auth.reset_new_password}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-neutral-300 ps-10 pe-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
                  data-testid="reset-password"
                />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="password"
                  required
                  minLength={8}
                  placeholder={t.auth.reset_confirm_password}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full border border-neutral-300 ps-10 pe-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
                  data-testid="reset-confirm"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="reset-error">{error}</div>
              )}

              <p className="text-xs text-neutral-500">{t.auth.reset_password_hint}</p>

              <button type="submit" disabled={busy} className="w-full btn-primary justify-center disabled:opacity-60" data-testid="reset-submit-btn">
                {busy ? t.common.loading : t.auth.reset_submit}
                <ArrowRight size={18} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
